// VisemeProvider.js
import React, { useCallback, useMemo, useRef, useState } from "react";
import { VisemeDataCtx, VisemeActionsCtx, CurrentVisemeCtx } from "./VisemeContext";

/**
 * Provides:
 *  - visemePairs: rolling queue (bounded) for visualizations
 *  - totalPairsEver: monotonic count for "Phoneme Combinations (TOTAL)"
 *  - nonZeroEvents (timestamps via getter): for Phonemes/min
 *  - recordVisemeChange(prev, cur): single entry point to record a viseme change
 *  - clearVisemePairs(): clears only the rolling queue (keeps totals/timestamps)
 *  - clearAllVisemeStats(): clears everything (use if you ever want a hard reset)
 *  - currentViseme / setCurrentViseme / visemeRef: live Idylate index, split so
 *    the studio root does not rerender on every viseme
 */
export default function VisemeProvider({ children }) {
  const MAX_ROLLING = 240;
  const GRID_SIZE = 33;
  const makeGrid = () =>
    Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));
  const [visemePairs, setVisemePairs] = useState([]);
  const totalPairsEverRef = useRef(0);
  const [totalPairsEver, setTotalPairsEver] = useState(0);

  const visemeRef = useRef(0);
  const visemeRecordingEnabledRef = useRef(true);
  const [currentViseme, setCurrentVisemeState] = useState(0);

  // We store only recent timestamps to bound memory (keep ~2 minutes)
  const nonZeroEventsRef = useRef([]);
  const [comboGridEver, setComboGridEver] = useState(() => makeGrid());

  const setCurrentViseme = useCallback((val) => {
    const cur = Math.max(0, Math.min(32, Number(val) || 0));
    visemeRef.current = cur;
    setCurrentVisemeState((prev) => (Object.is(prev, cur) ? prev : cur));
  }, []);
  
  const setVisemeRecordingEnabled = useCallback((enabled) => {
    visemeRecordingEnabledRef.current = enabled !== false;
  }, []);

  const recordVisemeChange = useCallback((prev, cur, nowMs = Date.now()) => {
    if (!visemeRecordingEnabledRef.current) return;
    if (prev === cur) return; // count only *changes*

    // 1) rolling queue for local visualizations
    setVisemePairs((prevArr) => {
      const next = [...prevArr, [prev, cur]];
      if (next.length > MAX_ROLLING) next.splice(0, next.length - MAX_ROLLING);
      return next;
    });

    // 2) monotonic "ever" total
    totalPairsEverRef.current += 1;
    setTotalPairsEver(totalPairsEverRef.current);

    // 3) timestamps for PPM (non-zero phoneme-class changes)
    if (cur !== 0) {
      const arr = nonZeroEventsRef.current;
      arr.push(nowMs);
      const cutoff = nowMs - 120_000;
      while (arr.length && arr[0] < cutoff) arr.shift();
    }

    // ✅ Update global 33×33 grid so every TwoVCombos sees the same counts
    setComboGridEver((g) => {
      if (
        prev < 0 || prev >= GRID_SIZE ||
        cur  < 0 || cur  >= GRID_SIZE
      ) return g;
      const next = g.map((row) => row.slice());
      next[prev][cur] += 1;
      return next;
    });
  }, []);

  const clearVisemePairs = useCallback(() => {
    setVisemePairs([]);
  }, []);

  const clearAllVisemeStats = useCallback(() => {
    setVisemePairs([]);
    totalPairsEverRef.current = 0;
    setTotalPairsEver(0);
    nonZeroEventsRef.current = [];
    setComboGridEver(makeGrid());
  }, []);

  // Return a *copy* so consumers can safely filter/count
  const getNonZeroEventTimes = useCallback(
    () => nonZeroEventsRef.current.slice(),
    []
  );

  const getTotalPairsEver = useCallback(
    () => totalPairsEverRef.current,
    []
  );

  const actions = useMemo(
    () => ({
      setVisemePairs,
      clearVisemePairs,
      clearAllVisemeStats,
      recordVisemeChange,
      setVisemeRecordingEnabled,
      getNonZeroEventTimes,
      getTotalPairsEver,
      setCurrentViseme,
      visemeRef,
    }),
    [
      clearVisemePairs,
      clearAllVisemeStats,
      recordVisemeChange,
      setVisemeRecordingEnabled,
      getNonZeroEventTimes,
      getTotalPairsEver,
      setCurrentViseme,
    ]
  );

  const data = useMemo(
    () => ({
      visemePairs,
      totalPairsEver,
      comboGridEver,
    }),
    [visemePairs, totalPairsEver, comboGridEver]
  );

  return (
    <VisemeActionsCtx.Provider value={actions}>
      <VisemeDataCtx.Provider value={data}>
        <CurrentVisemeCtx.Provider value={currentViseme}>
          {children}
        </CurrentVisemeCtx.Provider>
      </VisemeDataCtx.Provider>
    </VisemeActionsCtx.Provider>
  );
}
